import React, { useState, useMemo } from 'react';
import { IconQuant, IconTarget, IconAlertTriangle } from '../components/Icons';

// ============================================================================
// TYPES
// ============================================================================
interface KellyResults {
  payoffRatio: number;
  kellyPct: number;
  halfKellyPct: number;
  quarterKellyPct: number;
  kellyDollar: number;
  halfKellyDollar: number;
  quarterKellyDollar: number;
  kellyEdge: number;
  expectedValuePerTrade: number;
  rorFull: number;
  rorHalf: number;
  rorQuarter: number;
  isNegativeEdge: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function calcKelly(winRate: number, avgWinPct: number, avgLossPct: number): number {
  if (avgLossPct === 0) return 0;
  const payoffRatio = avgWinPct / avgLossPct;
  return (winRate * payoffRatio - (1 - winRate)) / payoffRatio;
}

function calcKellyEdge(winRate: number, payoffRatio: number): number {
  if (payoffRatio === 0) return 0;
  return winRate - (1 - winRate) / payoffRatio;
}

function calcRoR(portfolioValue: number, riskPerTrade: number, kellyEdge: number): number {
  if (riskPerTrade <= 0 || portfolioValue <= 0) return 1;
  const ratio = (1 - kellyEdge) / (1 + kellyEdge);
  if (ratio <= 0) return 0;
  if (ratio >= 1) return 1;
  const exponent = portfolioValue / riskPerTrade;
  return Math.pow(ratio, exponent);
}

function formatPct(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

function formatDollar(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatRoR(value: number): string {
  if (value < 0.0001) return '<0.01%';
  return formatPct(value, 2);
}

function getKellyColor(kellyPct: number): string {
  if (kellyPct > 0.5) return 'text-red-400';
  if (kellyPct > 0.25) return 'text-amber-400';
  return 'text-green-400';
}

function getGridCellStyle(kellyPct: number, isHighlighted: boolean): string {
  const base = 'text-xs font-mono text-center py-1.5 px-1 rounded transition-all duration-150 ';
  const ring = isHighlighted ? 'ring-2 ring-violet-400 ring-offset-1 ring-offset-gray-900 ' : '';
  if (kellyPct <= 0) return base + ring + 'bg-gray-700 text-gray-500';
  if (kellyPct > 0.5) return base + ring + 'bg-red-900/60 text-red-300';
  if (kellyPct > 0.25) return base + ring + 'bg-amber-900/60 text-amber-300';
  return base + ring + 'bg-green-900/60 text-green-300';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  onChange: (v: number) => void;
}

const SliderInput: React.FC<SliderInputProps> = ({
  label, value, min, max, step, displayValue, onChange,
}) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-center">
      <label className="text-xs font-medium text-gray-400">{label}</label>
      <span className="text-xs font-mono font-semibold text-violet-300 bg-violet-900/30 px-2 py-0.5 rounded">
        {displayValue}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gray-700
        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5
        [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full
        [&::-webkit-slider-thumb]:bg-violet-500 [&::-webkit-slider-thumb]:cursor-pointer
        [&::-webkit-slider-thumb]:hover:bg-violet-400 [&::-webkit-slider-thumb]:transition-colors"
    />
    <div className="flex justify-between text-[10px] text-gray-600">
      <span>{min}</span>
      <span>{max}</span>
    </div>
  </div>
);

interface ResultCardProps {
  label: string;
  kellyPct: number;
  dollarAmount: number;
  badge: { text: string; color: string };
  isNegative: boolean;
}

const ResultCard: React.FC<ResultCardProps> = ({
  label, kellyPct, dollarAmount, badge, isNegative,
}) => {
  const pctDisplay = isNegative ? 'No Bet' : formatPct(kellyPct, 2);
  const dollarDisplay = isNegative ? '—' : formatDollar(dollarAmount);
  const valueColor = isNegative ? 'text-gray-500' : getKellyColor(kellyPct);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700/60 p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-400">{label}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>
          {badge.text}
        </span>
      </div>
      <div className={`text-4xl font-bold font-mono tracking-tight ${valueColor}`}>
        {pctDisplay}
      </div>
      <div className="text-sm text-gray-400">
        Position size:{' '}
        <span className="font-semibold text-gray-200">{dollarDisplay}</span>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const KellySizingView: React.FC = () => {
  const [winRate, setWinRate] = useState(55);
  const [avgWinPct, setAvgWinPct] = useState(5);
  const [avgLossPct, setAvgLossPct] = useState(3);
  const [portfolioValue, setPortfolioValue] = useState(100000);
  const [maxTradesConcurrent, setMaxTradesConcurrent] = useState(5);

  const results: KellyResults = useMemo(() => {
    const wr  = winRate / 100;
    const awp = avgWinPct / 100;
    const alp = avgLossPct / 100;

    const payoffRatio    = alp > 0 ? awp / alp : 0;
    const rawKelly       = calcKelly(wr, awp, alp);
    const isNegativeEdge = rawKelly <= 0;

    const kellyPct        = Math.max(0, rawKelly);
    const halfKellyPct    = kellyPct / 2;
    const quarterKellyPct = kellyPct / 4;

    const kellyEdge            = calcKellyEdge(wr, payoffRatio);
    const expectedValuePerTrade = portfolioValue * (wr * awp - (1 - wr) * alp);

    const riskPerTrade        = (kellyPct * portfolioValue) / Math.max(1, maxTradesConcurrent);
    const halfRiskPerTrade    = riskPerTrade / 2;
    const quarterRiskPerTrade = riskPerTrade / 4;

    const rorFull    = isNegativeEdge ? 1    : calcRoR(portfolioValue, riskPerTrade,        kellyEdge);
    const rorHalf    = isNegativeEdge ? 0.5  : calcRoR(portfolioValue, halfRiskPerTrade,    kellyEdge);
    const rorQuarter = isNegativeEdge ? 0.25 : calcRoR(portfolioValue, quarterRiskPerTrade, kellyEdge);

    return {
      payoffRatio,
      kellyPct,
      halfKellyPct,
      quarterKellyPct,
      kellyDollar:        portfolioValue * kellyPct,
      halfKellyDollar:    portfolioValue * halfKellyPct,
      quarterKellyDollar: portfolioValue * quarterKellyPct,
      kellyEdge,
      expectedValuePerTrade,
      rorFull,
      rorHalf,
      rorQuarter,
      isNegativeEdge,
    };
  }, [winRate, avgWinPct, avgLossPct, portfolioValue, maxTradesConcurrent]);

  const gridWinRates     = [30, 40, 50, 60, 70];
  const gridPayoffRatios = [0.5, 1.0, 1.5, 2.0, 3.0];

  const gridData = useMemo(() => {
    return gridWinRates.map(wr => {
      return gridPayoffRatios.map(pr => {
        const wr_dec = wr / 100;
        const kelly  = (wr_dec * pr - (1 - wr_dec)) / pr;
        return Math.max(0, kelly);
      });
    });
  }, []);

  const currentPayoffCol = useMemo(() => {
    let closest = 0;
    let minDiff = Infinity;
    gridPayoffRatios.forEach((pr, i) => {
      const diff = Math.abs(pr - results.payoffRatio);
      if (diff < minDiff) { minDiff = diff; closest = i; }
    });
    return closest;
  }, [results.payoffRatio]);

  const maxDrawdownFull    = results.isNegativeEdge ? 0 : Math.min(1, 2 * Math.sqrt(results.kellyPct        * (1 - results.kellyPct)));
  const maxDrawdownHalf    = results.isNegativeEdge ? 0 : Math.min(1, 2 * Math.sqrt(results.halfKellyPct    * (1 - results.halfKellyPct)));
  const maxDrawdownQuarter = results.isNegativeEdge ? 0 : Math.min(1, 2 * Math.sqrt(results.quarterKellyPct * (1 - results.quarterKellyPct)));

  return (
    <div className="flex h-full bg-gray-950 text-gray-100 overflow-hidden">

      {/* LEFT COLUMN */}
      <div className="w-72 flex-shrink-0 bg-white/[0.03] border-r border-gray-800 flex flex-col overflow-y-auto">
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-violet-600/20 flex items-center justify-center flex-shrink-0">
              <IconQuant className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <div className="text-base font-bold text-white leading-tight">Kelly Sizer</div>
              <div className="text-[10px] text-gray-500 font-medium tracking-wide uppercase">
                Optimal Position Sizing
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5 flex-1">
          <SliderInput
            label="Win Rate"
            value={winRate}
            min={10}
            max={90}
            step={1}
            displayValue={`${winRate}%`}
            onChange={setWinRate}
          />
          <SliderInput
            label="Avg Win % per Trade"
            value={avgWinPct}
            min={0.5}
            max={30}
            step={0.5}
            displayValue={`${avgWinPct.toFixed(1)}%`}
            onChange={setAvgWinPct}
          />
          <SliderInput
            label="Avg Loss % per Trade"
            value={avgLossPct}
            min={0.5}
            max={30}
            step={0.5}
            displayValue={`${avgLossPct.toFixed(1)}%`}
            onChange={setAvgLossPct}
          />

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-400">Portfolio Value ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-mono">$</span>
              <input
                type="number"
                min={1000}
                step={1000}
                value={portfolioValue}
                onChange={e => setPortfolioValue(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-7 pr-3 py-2 text-sm
                  font-mono text-gray-100 focus:outline-none focus:border-violet-500 focus:ring-1
                  focus:ring-violet-500/30 transition-colors"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-400">Max Trades Concurrent</label>
            <input
              type="number"
              min={1}
              max={20}
              value={maxTradesConcurrent}
              onChange={e =>
                setMaxTradesConcurrent(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))
              }
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm
                font-mono text-gray-100 focus:outline-none focus:border-violet-500 focus:ring-1
                focus:ring-violet-500/30 transition-colors"
            />
            <p className="text-[10px] text-gray-600">Used for Risk of Ruin calculation</p>
          </div>

          <button
            className="w-full py-2.5 bg-violet-600 hover:bg-violet-500 active:bg-violet-700
              text-white font-semibold text-sm rounded-lg transition-colors duration-150
              flex items-center justify-center gap-2 shadow-lg shadow-violet-900/30"
          >
            <IconTarget className="w-4 h-4" />
            Calculate
          </button>

          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/40">
            <p className="text-[11px] text-gray-500 italic leading-relaxed">
              The Kelly Criterion finds the optimal bet fraction that maximizes long-run
              geometric growth of your portfolio. It balances maximizing gains against
              avoiding ruin — sizing too large is often more dangerous than sizing too small.
            </p>
          </div>
        </div>
      </div>

      {/* CENTER COLUMN */}
      <div className="flex-1 flex flex-col overflow-y-auto p-6 space-y-5 min-w-0">
        {results.isNegativeEdge && (
          <div className="flex items-center gap-3 bg-red-900/20 border border-red-700/40 rounded-xl p-4">
            <IconAlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <div className="text-sm font-semibold text-red-300">Negative Expected Value</div>
              <div className="text-xs text-red-400/80 mt-0.5">
                These parameters produce a negative Kelly — do not place this trade.
                Improve your win rate or payoff ratio.
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <ResultCard
            label="Full Kelly"
            kellyPct={results.kellyPct}
            dollarAmount={results.kellyDollar}
            badge={{ text: 'High Risk', color: 'bg-red-900/50 text-red-300 border border-red-700/40' }}
            isNegative={results.isNegativeEdge}
          />
          <ResultCard
            label="Half Kelly"
            kellyPct={results.halfKellyPct}
            dollarAmount={results.halfKellyDollar}
            badge={{ text: 'Recommended', color: 'bg-amber-900/50 text-amber-300 border border-amber-700/40' }}
            isNegative={results.isNegativeEdge}
          />
          <ResultCard
            label="Quarter Kelly"
            kellyPct={results.quarterKellyPct}
            dollarAmount={results.quarterKellyDollar}
            badge={{ text: 'Conservative', color: 'bg-green-900/50 text-green-300 border border-green-700/40' }}
            isNegative={results.isNegativeEdge}
          />
        </div>

        <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-5">
          <div className="flex items-center gap-2 mb-4">
            <IconQuant className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold text-gray-200">Kelly Breakdown</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/40">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Payoff Ratio</div>
              <div className="text-2xl font-bold font-mono text-gray-100">
                {results.payoffRatio.toFixed(2)}
                <span className="text-sm font-normal text-gray-500">:1</span>
              </div>
              <div className="text-[10px] text-gray-600 mt-1">Avg Win / Avg Loss</div>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/40">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Kelly Edge</div>
              <div className={`text-2xl font-bold font-mono ${results.kellyEdge < 0 ? 'text-red-400' : 'text-violet-300'}`}>
                {formatPct(results.kellyEdge, 2)}
              </div>
              <div className="text-[10px] text-gray-600 mt-1">WR − (1−WR)/Payoff</div>
            </div>
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/40">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">EV per Trade</div>
              <div className={`text-2xl font-bold font-mono ${results.expectedValuePerTrade < 0 ? 'text-red-400' : 'text-green-400'}`}>
                {results.expectedValuePerTrade < 0 ? '-' : '+'}
                {formatDollar(Math.abs(results.expectedValuePerTrade))}
              </div>
              <div className="text-[10px] text-gray-600 mt-1">On full portfolio basis</div>
            </div>
          </div>
          <div className="mt-4 bg-gray-950/50 rounded-lg p-3 font-mono text-[11px] text-gray-500 border border-gray-800">
            <span className="text-violet-400">kelly</span>
            {' = '}
            <span className="text-gray-300">
              ({winRate}% × {results.payoffRatio.toFixed(2)} − {100 - winRate}%) / {results.payoffRatio.toFixed(2)}
            </span>
            {' = '}
            <span className={results.isNegativeEdge ? 'text-red-400' : 'text-green-400'}>
              {results.isNegativeEdge
                ? formatPct((winRate / 100 * results.payoffRatio - (1 - winRate / 100)) / (results.payoffRatio || 1), 2)
                : formatPct(results.kellyPct, 2)}
            </span>
          </div>
        </div>

        <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-5">
          <div className="flex items-center gap-2 mb-4">
            <IconAlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-200">Risk of Ruin</h3>
            <span className="text-[10px] text-gray-600 ml-1">— probability of total account wipeout</span>
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-700/40">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900/60 border-b border-gray-700/40">
                  <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Sizing Method</th>
                  <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Ruin Probability</th>
                  <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2.5">Max Drawdown (est.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {[
                  { label: 'Full Kelly',    badge: 'High Risk',    badgeClass: 'bg-red-900/50 text-red-300',    ror: results.rorFull,    maxDD: maxDrawdownFull,    ddColor: 'text-red-400' },
                  { label: 'Half Kelly',    badge: 'Recommended',  badgeClass: 'bg-amber-900/50 text-amber-300', ror: results.rorHalf,    maxDD: maxDrawdownHalf,    ddColor: 'text-amber-400' },
                  { label: 'Quarter Kelly', badge: 'Conservative', badgeClass: 'bg-green-900/50 text-green-300', ror: results.rorQuarter, maxDD: maxDrawdownQuarter, ddColor: 'text-green-400' },
                ].map(row => (
                  <tr key={row.label} className="hover:bg-gray-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-200 font-medium text-sm">{row.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${row.badgeClass}`}>{row.badge}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-mono font-semibold text-sm ${
                        results.isNegativeEdge ? 'text-red-400' :
                        row.ror > 0.1  ? 'text-red-400' :
                        row.ror > 0.01 ? 'text-amber-400' : 'text-green-400'
                      }`}>
                        {results.isNegativeEdge ? '100%' : formatRoR(row.ror)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-mono font-semibold text-sm ${row.ddColor}`}>
                        {results.isNegativeEdge ? '—' : formatPct(row.maxDD, 1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-600 mt-2 italic">
            RoR = ((1 − edge) / (1 + edge))^(portfolio / risk_per_trade). Max drawdown is a statistical estimate.
          </p>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="w-80 flex-shrink-0 bg-white/[0.02] border-l border-gray-800 flex flex-col overflow-y-auto p-5 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <IconTarget className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-semibold text-gray-200">Win Rate × Payoff Grid</h3>
          </div>
          <p className="text-[10px] text-gray-600 mb-3">Kelly % sensitivity across scenarios. Your position is highlighted.</p>
          <div className="bg-gray-900/40 rounded-xl border border-gray-700/40 p-3">
            <div className="grid mb-1" style={{ gridTemplateColumns: '2.75rem repeat(5, 1fr)', gap: '3px' }}>
              <div className="text-[9px] text-gray-600 flex items-end pb-1 leading-tight">WR\PR</div>
              {gridPayoffRatios.map(pr => (
                <div key={pr} className="text-[9px] font-mono text-gray-500 text-center py-1">{pr}:1</div>
              ))}
            </div>
            <div className="space-y-0.5">
              {gridWinRates.map((wr, rowIdx) => {
                const isCurrentRow = Math.abs(wr - winRate) < 5.5;
                return (
                  <div key={wr} className="grid items-center" style={{ gridTemplateColumns: '2.75rem repeat(5, 1fr)', gap: '3px' }}>
                    <div className={`text-[9px] font-mono text-right pr-2 ${isCurrentRow ? 'text-violet-400 font-bold' : 'text-gray-500'}`}>
                      {wr}%
                    </div>
                    {gridPayoffRatios.map((_, colIdx) => {
                      const kelly = gridData[rowIdx][colIdx];
                      const isHighlighted = isCurrentRow && colIdx === currentPayoffCol;
                      return (
                        <div key={colIdx} className={getGridCellStyle(kelly, isHighlighted)}>
                          {kelly <= 0 ? '—' : `${(kelly * 100).toFixed(0)}%`}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 pt-2.5 border-t border-gray-700/40">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-green-900/80 border border-green-700/50" />
                <span className="text-[9px] text-gray-600">&lt;25%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-amber-900/80 border border-amber-700/50" />
                <span className="text-[9px] text-gray-600">25–50%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm bg-red-900/80 border border-red-700/50" />
                <span className="text-[9px] text-gray-600">&gt;50%</span>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                <div className="w-2.5 h-2.5 rounded-sm border-2 border-violet-400" />
                <span className="text-[9px] text-gray-600">You</span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <IconAlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-200">Practical Guidelines</h3>
          </div>
          <div className="space-y-2.5">
            {[
              { icon: '!', iconBg: 'bg-red-900/40 text-red-400', title: 'Never Risk More Than 2–5%', body: 'Cap position size regardless of what Kelly suggests. Extreme Kelly fractions assume perfect parameter estimates.' },
              { icon: '~', iconBg: 'bg-amber-900/40 text-amber-400', title: 'Kelly Assumes Independence', body: 'The formula assumes all trades are statistically independent. Correlated positions silently inflate your true risk.' },
              { icon: '½', iconBg: 'bg-violet-900/40 text-violet-400', title: 'Use Half Kelly in Practice', body: 'Slippage, commissions, and estimation error mean Half Kelly delivers nearly optimal growth with far less volatility.' },
            ].map(item => (
              <div key={item.title} className="bg-gray-800/50 border border-gray-700/40 rounded-lg p-3 flex gap-3">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${item.iconBg}`}>
                  {item.icon}
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-200 mb-1">{item.title}</div>
                  <div className="text-[11px] text-gray-500 leading-relaxed">{item.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-violet-900/10 border border-violet-800/30 rounded-xl p-4">
          <div className="text-[10px] font-semibold text-violet-400 uppercase tracking-wider mb-3">
            Current Parameters
          </div>
          <div className="space-y-2">
            {[
              ['Win Rate',     `${winRate}%`],
              ['Avg Win',      `${avgWinPct.toFixed(1)}%`],
              ['Avg Loss',     `${avgLossPct.toFixed(1)}%`],
              ['Payoff Ratio', `${results.payoffRatio.toFixed(2)}:1`],
              ['Portfolio',    formatDollar(portfolioValue)],
              ['Concurrent',   `${maxTradesConcurrent} trades`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-center">
                <span className="text-[11px] text-gray-500">{k}</span>
                <span className="text-[11px] font-mono font-semibold text-gray-300">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KellySizingView;
